import { useCallback, useMemo } from 'react';
import { odata } from '@bingads-webui/http-util';
import { useOdataOptions } from './use-odata-options';
import type { AigcRequestActivity } from './use-generate-business-snapshot';

// --- API: POST generate-campaign-recommendation ---

export interface BrandContext {
  logos: string[];
  images: string[];
  brandVoice: string;
  messageRestrictions: string[];
  termExclusions: string[];
}

export interface BusinessContext {
  businessName: string;
  businessLocation: string;
  businessLinks: string[];
  offerings: string;
  customers: string;
  advertisingGoals: string;
}

export interface GenerateCampaignRecommendationRequest {
  locale?: string;
  brandContext: BrandContext;
  businessContext: BusinessContext;
}

export interface GenerateCampaignRecommendationPostResponse {
  Data: {
    TaskId: string;
    Categories?: string[];
  };
  Errors: unknown;
}

const API_NAME = 'odataapi/campaign/v2/accountsv2/generatecampaignrecommendation';

export const usePostGenerateCampaignRecommendation = () => {
  const [odataOptions, { cid, aid }] = useOdataOptions('Advertiser/V2');

  const url = useMemo(() => (
    `AccountsV2/GenerateCampaignRecommendation(CustomerId=${cid},AccountId=${aid})`
  ), [cid, aid]);

  return useCallback((
    request: GenerateCampaignRecommendationRequest,
    activity?: AigcRequestActivity
  ) => {
    const requestBody = {
      locale: request.locale || 'en-US',
      brandContext: request.brandContext,
      businessContext: request.businessContext,
    };

    // Log request + response/error from a single jQuery `complete` callback
    // so all three trace lines carry the same x-ms-requestid (extracted
    // from the response headers).
    const responsePromise = odata.post({
      ...odataOptions,
      url,
      data: requestBody,
      complete: (jqXHR: any, textStatus: string) => {
        if (!activity) return;
        const requestId =
          (jqXHR && typeof jqXHR.getResponseHeader === 'function')
            ? (jqXHR.getResponseHeader('x-ms-requestid') ?? undefined)
            : undefined;
        const isSuccess = textStatus === 'success'
          || textStatus === 'notmodified'
          || textStatus === 'nocontent';

        activity.trace(
          `[PMaxLiteSignup][AgenticOnboarding] GenerateCampaignRecommendation request: ${JSON.stringify(requestBody)}`,
          API_NAME,
          requestId
        );

        const responseBody = jqXHR?.responseJSON;
        if (isSuccess && responseBody?.Data?.TaskId) {
          activity.trace(
            `[PMaxLiteSignup][AgenticOnboarding] GenerateCampaignRecommendation response: taskId=${responseBody?.Data?.TaskId ?? ''}`,
            API_NAME,
            requestId
          );
        } else {
          const errorBody = isSuccess
            ? responseBody?.Errors ?? responseBody
            : responseBody ?? jqXHR?.responseText ?? jqXHR?.statusText ?? textStatus;
          activity.error(
            `[PMaxLiteSignup][AgenticOnboarding] GenerateCampaignRecommendation failed: httpStatus=${jqXHR?.status} textStatus=${textStatus} ${JSON.stringify(errorBody)}`,
            API_NAME,
            requestId
          );
        }
      },
    } as any) as Promise<GenerateCampaignRecommendationPostResponse>;

    return responsePromise.then((response) => {
      const responseBody = (response as any)?.data ?? response;
      if (responseBody?.Data?.TaskId) return response;

      const errorDetail = JSON.stringify(responseBody?.Errors ?? responseBody);
      throw new Error(`GenerateCampaignRecommendation returned no taskId: ${errorDetail}`);
    });
  }, [odataOptions, url]);
};
